/**
 * ViewExpenseModal Component
 * 
 * This component provides a detailed view modal for expense records.
 * It displays comprehensive expense information with a well-organized layout.
 * 
 * Features:
 * - Complete expense details view
 * - Category and payment method information  
 * - Receipt viewing and download
 * - Amount breakdown with VAT
 * - Status tracking and history
 * - Full internationalization support (Arabic/English)
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  XMarkIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftEllipsisIcon
} from '@heroicons/react/24/outline';

const ViewExpenseModal = ({ expense, onClose, onEdit }) => {
  const { t } = useTranslation();
  
  const expenseCategories = [
    { id: 'office', name: t('expenses.office_expenses', 'Office Expenses'), icon: '🏢' },
    { id: 'marketing', name: t('expenses.marketing_expenses', 'Marketing'), icon: '📢' },
    { id: 'salary', name: t('expenses.salary_expenses', 'Salaries'), icon: '💰' },
    { id: 'utility', name: t('expenses.utility_expenses', 'Utilities'), icon: '⚡' },
    { id: 'equipment', name: t('expenses.equipment_expenses', 'Equipment'), icon: '🔧' },
    { id: 'legal', name: t('expenses.legal_expenses', 'Legal'), icon: '⚖️' },
    { id: 'travel', name: t('expenses.travel_expenses', 'Travel'), icon: '✈️' },
    { id: 'insurance', name: t('expenses.insurance_expenses', 'Insurance'), icon: '🛡️' },
    { id: 'maintenance', name: t('expenses.maintenance_expenses', 'Maintenance'), icon: '🔨' },
    { id: 'fuel', name: t('expenses.fuel_expenses', 'Fuel Expenses'), icon: '⛽' },
    { id: 'truck_washing', name: t('expenses.truck_washing_expenses', 'Truck Washing'), icon: '🚿' },
    { id: 'oil_change', name: t('expenses.oil_change_expenses', 'Oil Changes'), icon: '🛢️' },
    { id: 'other', name: t('expenses.other_expenses', 'Other'), icon: '📋' }
  ];

  const paymentMethods = [
    { id: 'cash', name: t('expenses.payment.cash', 'Cash'), icon: '💵' },
    { id: 'card', name: t('expenses.payment.card', 'Credit Card'), icon: '💳' },
    { id: 'bank_transfer', name: t('expenses.payment.bank_transfer', 'Bank Transfer'), icon: '🏦' },
    { id: 'check', name: t('expenses.payment.check', 'Check'), icon: '📝' },
    { id: 'online', name: t('expenses.payment.online', 'Online Payment'), icon: '🌐' }
  ];

  const getCategoryInfo = (categoryId) => {
    return expenseCategories.find(cat => cat.id === categoryId) || expenseCategories[12];
  };

  const getPaymentMethodInfo = (methodId) => {
    return paymentMethods.find(method => method.id === methodId) || paymentMethods[0];
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'badge badge-warning', text: t('accounting.pending_approval', 'Pending Approval') },
      approved: { class: 'badge badge-success', text: t('accounting.approved', 'Approved') },
      rejected: { class: 'badge badge-error', text: t('quotations.rejected') },
      paid: { class: 'badge badge-primary', text: t('common.paid') }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={config.class}>{config.text}</span>;
  };

  const categoryInfo = getCategoryInfo(expense.category);
  const paymentMethodInfo = getPaymentMethodInfo(expense.paymentMethod);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-900">{t('expenses.view_expense', 'View Expense')}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {t('expenses.expense_id', 'Expense ID')}: 
                <span className="font-mono text-blue-600 ml-1">{expense.id}</span>
              </p>
            </div>
            <button 
              className="p-2 hover:bg-gray-100 rounded-full transition-colors" 
              onClick={onClose}
            >
              <XMarkIcon className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Header Info */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="text-xl font-semibold text-gray-900 mb-2">{expense.description}</h4>
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">{categoryInfo.icon}</span>
                  <span className="text-lg font-medium text-gray-700">{categoryInfo.name}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="mb-3">{getStatusBadge(expense.status)}</div>
                <div className="text-3xl font-bold text-blue-600">SAR {expense.totalAmount?.toFixed(2) || expense.amount?.toFixed(2)}</div>
              </div>
            </div>
          </div>

          {/* Expense Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h5 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <InformationCircleIcon className="w-5 h-5 mr-2 text-blue-500" />
                {t('expenses.basic_information', 'Basic Information')}
              </h5>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600">{t('expenses.date', t('common.date'))}</span>
                  <span className="font-semibold">{new Date(expense.date).toLocaleDateString()}</span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600">{t('expenses.vendor', 'Vendor')}</span>
                  <span className="font-semibold">{expense.vendor || t('common.not_specified', 'Not specified')}</span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600">{t('expenses.reference', 'Reference')}</span>
                  <span className="font-semibold">{expense.reference || t('common.not_specified', 'Not specified')}</span>
                </div>

                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-gray-600">{t('expenses.payment_method', 'Payment Method')}</span>
                  <div className="flex items-center space-x-2">
                    <span>{paymentMethodInfo.icon}</span>
                    <span className="font-semibold">{paymentMethodInfo.name}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Receipt & Submission Info */}
            <div className="bg-green-50 rounded-lg p-6">
              <h5 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <DocumentTextIcon className="w-5 h-5 mr-2 text-green-500" />
                {t('expenses.receipt_information', 'Receipt & Submission')}
              </h5>
              
              <div className="space-y-4">
                <div className="py-2 border-b border-green-200">
                  <span className="text-sm font-medium text-gray-600 block mb-2">{t('expenses.receipt', 'Receipt')}</span>
                  {expense.receiptAttached ? (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-green-600 text-lg">📄</span>
                        <span className="text-green-600 font-semibold">{t('expenses.receipt_attached', 'Receipt attached')}</span>
                      </div>
                      {expense.receiptFileName && (
                        <div className="text-sm text-gray-600 bg-white rounded px-3 py-2">
                          📎 {expense.receiptFileName}
                        </div>
                      )}
                      {expense.receiptUrl && (
                        <div>
                          <a 
                            href={expense.receiptUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm transition-colors"
                          >
                            👁️ {t('expenses.view_receipt', 'View Receipt')} ↗
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span className="text-red-600 text-lg">📋</span>
                      <span className="text-red-600 font-semibold">{t('expenses.no_receipt', 'No receipt')}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between py-2 border-b border-green-200">
                  <span className="text-sm font-medium text-gray-600">{t('expenses.submitted_by', 'Submitted By')}</span>
                  <span className="font-semibold">{expense.createdBy || t('common.not_specified', 'Not specified')}</span>
                </div>

                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-gray-600">{t('expenses.submitted_at', 'Submitted At')}</span>
                  <span className="font-semibold">
                    {expense.submittedAt ? new Date(expense.submittedAt).toLocaleString() : t('common.not_specified', 'Not specified')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Amount Breakdown */}
          <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h5 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CurrencyDollarIcon className="w-5 h-5 mr-2 text-blue-500" />
              {t('expenses.amount_breakdown', 'Amount Breakdown')}
            </h5>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-gray-900">SAR {expense.amount?.toFixed(2) || '0.00'}</div>
                <div className="text-sm text-gray-600 mt-1">{t('expenses.subtotal', 'Subtotal')}</div>
              </div>
              
              <div className="bg-white rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">SAR {expense.vatAmount?.toFixed(2) || '0.00'}</div>
                <div className="text-sm text-gray-600 mt-1">{t('expenses.vat_amount', 'VAT Amount')} ({expense.vatRate || 0}%)</div>
              </div>
              
              <div className="bg-white rounded-lg p-4 text-center border-2 border-blue-500">
                <div className="text-3xl font-bold text-blue-600">SAR {expense.totalAmount?.toFixed(2) || expense.amount?.toFixed(2) || '0.00'}</div>
                <div className="text-sm text-gray-600 mt-1">{t('expenses.total_amount', 'Total Amount')}</div>
              </div>
            </div>
          </div>

          {/* Description & Notes */}
          <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
            <h5 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ChatBubbleLeftEllipsisIcon className="w-5 h-5 mr-2 text-purple-500" />
              {t('expenses.additional_information', 'Additional Information')}
            </h5>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">{t('expenses.description', t('common.description'))}</label>
                <div className="bg-white p-4 rounded-lg border border-purple-200 min-h-[60px]">
                  {expense.description}
                </div>
              </div>

              {expense.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-2">{t('expenses.notes', t('common.notes'))}</label>
                  <div className="bg-white p-4 rounded-lg border border-purple-200 min-h-[60px]">
                    {expense.notes}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button 
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium" 
              onClick={onClose}
            >
              {t('common.close', t('common.close'))}
            </button>
            <button 
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium flex items-center space-x-2" 
              onClick={onEdit}
            >
              <span>✏️</span>
              <span>{t('expenses.edit_expense', 'Edit Expense')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewExpenseModal;
